import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { HardHat, ExternalLink, UserCog, Wrench, MapPin, BarChart3 } from 'lucide-react';

const STATUS_STYLES = {
  da_pianificare: { label: 'Da pianificare', variant: 'secondary' },
  pianificato: { label: 'Pianificato', variant: 'default' },
  in_corso: { label: 'In corso', variant: 'default' },
  completato: { label: 'Completato', variant: 'outline' },
  verificato: { label: 'Verificato', variant: 'outline' },
  annullato: { label: 'Annullato', variant: 'destructive' },
};

export default function Installations() {
  const [items, setItems] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [installerFilter, setInstallerFilter] = useState('all');
  const [aggr, setAggr] = useState(null);

  // Assign dialog
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignInstallerId, setAssignInstallerId] = useState('');
  const [assignDate, setAssignDate] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (installerFilter !== 'all') params.installer_id = installerFilter;
      const [r, agg, ins] = await Promise.all([
        api.listInstallations(params),
        api.installationsAggregations().catch(() => null),
        api.listInstallers().catch(() => []),
      ]);
      setItems(r.items || []);
      setAggr(agg);
      setInstallers(ins);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, installerFilter]);

  const openAssign = (it) => {
    setAssignTarget(it);
    setAssignInstallerId(it.installer_id || '');
    setAssignDate(it.planned_date || '');
  };
  const saveAssign = async () => {
    if (!assignInstallerId) { toast.error('Seleziona un installatore'); return; }
    setAssignBusy(true);
    try {
      await api.assignInstallation(assignTarget.id, {
        installer_id: assignInstallerId,
        planned_date: assignDate || null,
      });
      toast.success('Intervento assegnato');
      setAssignTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore assegnazione');
    } finally { setAssignBusy(false); }
  };

  const percentChecklist = (it) => {
    const cl = it.checklist || [];
    if (!cl.length) return 0;
    const done = cl.filter(x => x.status === 'uploaded' || x.status === 'verified').length;
    return Math.round(100 * done / cl.length);
  };

  return (
    <div>
      <PageHeader
        title="Gestione Interventi"
        description="Assegna installatori, pianifica date, verifica la documentazione caricata dai tecnici sul campo."
        actions={
          aggr && (
            <div className="flex gap-3 text-xs">
              <div><span className="text-muted-foreground">Totali:</span> <strong>{aggr.total_count}</strong></div>
              {(aggr.by_status || []).map(s => (
                <div key={s.key}><span className="text-muted-foreground">{s.key.replace('_', ' ')}:</span> <strong>{s.count}</strong></div>
              ))}
            </div>
          )
        }
      />

      <Card>
        <div className="p-3 border-b flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-status"><SelectValue placeholder="Stato…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="da_pianificare">Da pianificare</SelectItem>
              <SelectItem value="pianificato">Pianificato</SelectItem>
              <SelectItem value="in_corso">In corso</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
              <SelectItem value="verificato">Verificato</SelectItem>
            </SelectContent>
          </Select>
          <Select value={installerFilter} onValueChange={setInstallerFilter}>
            <SelectTrigger className="w-[220px]" data-testid="filter-installer"><SelectValue placeholder="Installatore…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli installatori</SelectItem>
              {installers.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <div className="text-xs text-muted-foreground">{items.length} interventi</div>
        </div>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <Wrench className="size-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm">Nessun intervento trovato con questi filtri</div>
            </div>
          ) : (
            <Table data-testid="installations-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Località</TableHead>
                  <TableHead>Installatore</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Checklist</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => {
                  const st = STATUS_STYLES[it.status] || { label: it.status, variant: 'secondary' };
                  const cust = it.customer_snapshot || {};
                  const custName = cust.customer_type === 'azienda'
                    ? cust.company_name
                    : `${cust.first_name || ''} ${cust.last_name || ''}`.trim();
                  const perc = percentChecklist(it);
                  return (
                    <TableRow key={it.id} data-testid={`installation-row-${it.id}`}>
                      <TableCell className="font-mono text-xs">{it.number || '—'}</TableCell>
                      <TableCell className="font-medium">{custName || '—'}</TableCell>
                      <TableCell><span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="size-3" />{it.site_city}, {it.site_province}</span></TableCell>
                      <TableCell>{it.installer_name ? <span className="text-sm">{it.installer_name}</span> : <Badge variant="secondary" className="text-[10px]">Non assegnato</Badge>}</TableCell>
                      <TableCell><span className="text-xs">{it.planned_date ? new Date(it.planned_date).toLocaleDateString('it-IT') : <span className="text-muted-foreground">—</span>}</span></TableCell>
                      <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${perc}%` }} />
                          </div>
                          <span className="text-xs tabular-nums text-muted-foreground">{perc}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => openAssign(it)} data-testid={`assign-${it.id}`}>
                            <UserCog className="size-3.5 mr-1" />Assegna
                          </Button>
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/installer/interventi/${it.id}`} data-testid={`open-${it.id}`}>
                              <ExternalLink className="size-3.5" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog assegna */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assegna intervento {assignTarget?.number}</DialogTitle>
            <DialogDescription>Seleziona l'installatore e (opzionalmente) la data pianificata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Installatore</Label>
              <Select value={assignInstallerId} onValueChange={setAssignInstallerId}>
                <SelectTrigger data-testid="assign-installer-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {installers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data pianificata (opzionale)</Label>
              <Input type="date" value={assignDate} onChange={e => setAssignDate(e.target.value)} data-testid="assign-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)} disabled={assignBusy}>Annulla</Button>
            <Button onClick={saveAssign} disabled={!assignInstallerId || assignBusy} data-testid="assign-save">Assegna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
